/**
 * Responsibility: Implement actor check behavior for the engine layer.
 * Scope: Owns engine-layer concerns only; cross-layer orchestration is excluded.
 */
import type { SupervisorCheckFn, SupervisorIncidentSeed } from "@anyquest/shared-core"
import { getVitalMax, VITAL_KEYS } from '../../vitals'

function isValidVital(value: unknown, max: number): boolean {
  const n = Number(value)
  return Number.isFinite(n) && n >= 0 && n <= max
}

export const actorCheck: SupervisorCheckFn = (state) => {
  const incidents: SupervisorIncidentSeed[] = []

  if (!String(state.player.identity ?? '').trim()) {
    incidents.push({
      code: 'actor_player_identity_missing',
      domain: 'actor',
      severity: 'high',
      confidence: 0.98,
      message: 'Player identity is empty.',
      remediationPlan: 'Set fallback player identity based on metadata.',
    })
  }

  const vitals = state.player.vitals
  const hasInvalidVital = VITAL_KEYS.some((key) => !isValidVital(vitals?.[key], getVitalMax(vitals, key)))
    || !Number.isFinite(vitals?.maxVitality) || vitals.maxVitality < 1
    || !Number.isFinite(vitals?.maxSpirit) || vitals.maxSpirit < 1
    || !Number.isFinite(vitals?.maxEssence) || vitals.maxEssence < 1
  if (hasInvalidVital) {
    incidents.push({
      code: 'actor_player_vitals_out_of_range',
      domain: 'actor',
      severity: 'high',
      confidence: 0.99,
      message: 'Player vitals contain invalid values.',
      remediationPlan: 'Clamp player vitals into 0..max ranges and repair invalid max values.',
      context: {
        vitality: vitals?.vitality,
        spirit: vitals?.spirit,
        essence: vitals?.essence,
        maxVitality: vitals?.maxVitality,
        maxSpirit: vitals?.maxSpirit,
        maxEssence: vitals?.maxEssence,
      },
    })
  }

  const invalidCompanions = (state.companions ?? []).filter(c => {
    const name = String(c?.name ?? '').trim()
    const identity = String(c?.identity ?? '').trim()
    return !name || !identity
  }).length

  if (invalidCompanions > 0) {
    incidents.push({
      code: 'actor_invalid_companion_entries',
      domain: 'actor',
      severity: 'medium',
      confidence: 0.95,
      message: `${invalidCompanions} companion entries are missing name/identity.`,
      remediationPlan: 'Prune invalid companion rows.',
      context: { invalidCompanions },
    })
  }

  return { passed: incidents.length === 0, incidents }
}
